import React from 'react';
import { FaPlus, FaCog } from 'react-icons/fa';
import { AdminLayout } from '../components/layout';
import { PageState } from '../components/common';
import { OptionTypeCard, OptionForm, OptionDeleteModal } from '../components/options';
import { useOptionManagement, useOptionForm, useOptionModals, useToast } from '../hooks';

const OptionManagement: React.FC = () => {
  // Custom hooks
  const {
    optionTypes,
    loading,
    error,
    loadOptionTypes,
    createOptionType,
    updateOptionType,
    deleteOptionType,
    toggleActive,
  } = useOptionManagement();

  const {
    formData,
    optionInput,
    editingOption,
    isFormValid,
    resetForm,
    setEditForm,
    updateFormData,
    setOptionInput,
    addOption,
    removeOption,
    validateForm,
  } = useOptionForm();

  const {
    showAddModal,
    deleteConfirm,
    openAddModal,
    closeAddModal,
    openDeleteModal,
    closeDeleteModal,
  } = useOptionModals();

  const { ToastComponent } = useToast();

  // Event handlers
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const success = editingOption
      ? await updateOptionType(editingOption.id, formData)
      : await createOptionType(formData);

    if (success) {
      resetForm();
      closeAddModal();
    }
  };

  const handleEdit = (option: any) => {
    setEditForm(option);
    openAddModal();
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      return;
    }

    const success = await deleteOptionType(deleteConfirm.id);
    if (success) {
      closeDeleteModal();
    }
  };

  const handleAddModalOpen = () => {
    resetForm();
    openAddModal();
  };

  const handleAddModalClose = () => {
    resetForm();
    closeAddModal();
  };

  return (
    <AdminLayout title="Seçenek Yönetimi">
      <PageState
        loading={loading}
        error={error || undefined}
        loadingStateProps={{
          size: 'lg',
          text: 'Seçenek türleri yükleniyor...',
          variant: 'admin',
          skeleton: true,
          skeletonType: 'list',
          skeletonProps: { items: 6 },
        }}
        errorStateProps={{
          title: 'Seçenek Türleri Yükleme Hatası',
          variant: 'admin',
          onRetry: loadOptionTypes,
          retryText: 'Yeniden Yükle',
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <FaCog className="text-blue-500" />
              Seçenek Türleri ({optionTypes.length})
            </h2>
            <p className="text-gray-600 mt-1">
              Ürünler için kullanılacak seçenek türlerini yönetin
            </p>
          </div>

          <button
            onClick={handleAddModalOpen}
            className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105"
          >
            <FaPlus />
            Yeni Seçenek Türü
          </button>
        </div>

        {/* Option Types List */}
        <div className="grid gap-4">
          {optionTypes.map(option => (
            <OptionTypeCard
              key={option.id}
              option={option}
              onEdit={handleEdit}
              onDelete={openDeleteModal}
              onToggleActive={toggleActive}
            />
          ))}

          {optionTypes.length === 0 && !loading && (
            <div className="text-center py-12">
              <FaCog className="text-gray-300 text-6xl mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">Henüz seçenek türü yok</h3>
              <p className="text-gray-500 mb-6">İlk seçenek türünüzü ekleyerek başlayın</p>
              <button
                onClick={handleAddModalOpen}
                className="bg-blue-500 text-white px-6 py-3 rounded-xl hover:bg-blue-600 transition-all"
              >
                Seçenek Türü Ekle
              </button>
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <OptionForm
              formData={formData}
              optionInput={optionInput}
              editingOption={editingOption}
              isFormValid={isFormValid}
              onFormDataChange={updateFormData}
              onOptionInputChange={setOptionInput}
              onAddOption={addOption}
              onRemoveOption={removeOption}
              onSubmit={handleSubmit}
              onCancel={handleAddModalClose}
            />
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <OptionDeleteModal
              option={deleteConfirm}
              onConfirm={handleDelete}
              onCancel={closeDeleteModal}
            />
          </div>
        )}
      </PageState>

      {ToastComponent}
    </AdminLayout>
  );
};

export default OptionManagement;
